/**
 * "O caminho do carrinho" / "O caminho de quem venceu": tres degraus.
 *
 * Nao e o `FunnelChart` de barras porque aqui sao TRES DESTINOS (entrou,
 * pagou, ficou fora), nao etapas aninhadas -- o segundo e o terceiro degrau
 * somados dao o primeiro. Cada degrau e um painel tingido pelo desfecho e o
 * percentual e sempre em relacao a quem entrou, escrito no canto.
 */

import { Badge, Footnote, Icon, Panel, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import { degrausDoCaminho } from '../agregacao';
import type { FunnelKind, OfferMetrics } from '../types';
import { VOCABULARIO } from '../vocabulario';

export function CaminhoDoFunil({ kind, metrics }: { readonly kind: FunnelKind; readonly metrics: OfferMetrics }): JSX.Element {
  const v = VOCABULARIO[kind];
  const degraus = degrausDoCaminho(kind, metrics, formatBRLFromCents, formatCount);

  return (
    <SectionCard id="caminho-do-funil" title={v.caminhoTitulo} icon={v.icone} aside={<Footnote>Cada % é em relação a quem entrou.</Footnote>}>
      <ol className="grid gap-3 md:grid-cols-3">
        {degraus.map((d, i) => (
          <li key={d.key} className="relative">
            <Panel tone={d.tone} className="relative flex h-full flex-col items-center justify-center gap-1 px-4 pb-5 pt-9 text-center">
              <span className="absolute left-4 top-3 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{d.eyebrow}</span>
              {d.ratio === null ? null : (
                <Badge tone={d.tone} className="absolute right-3 top-2.5">
                  {formatRatio(d.ratio.part, d.ratio.whole)}
                </Badge>
              )}
              <p className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{formatCount(d.value)}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{d.label}</p>
              <p className="text-xs text-ink-subtle">{d.detail}</p>
            </Panel>
            {i < degraus.length - 1 ? (
              <span aria-hidden="true" className="absolute -right-3 top-1/2 hidden -translate-y-1/2 text-ink-subtle md:block">
                <Icon name="seta-dir" size="sm" />
              </span>
            ) : null}
          </li>
        ))}
      </ol>

      <div className="mt-4 flex flex-col gap-1.5">
        {v.notasDoCaminho.map((nota) => (
          <Footnote key={nota}>{nota}</Footnote>
        ))}
      </div>

      {kind === 'expirados' && metrics.returnedAlone > 0 ? (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-line bg-elevated px-4 py-3 text-sm text-ink-muted">
          <Icon name="usuarios" size="sm" className="mt-0.5 text-ink-subtle" />
          <span>
            <strong className="tabular-nums text-ink">{formatCount(metrics.returnedAlone)}</strong> voltaram sozinhos ({formatBRLFromCents(metrics.returnedAloneAmountCents)}) — pagaram de novo
            sem passar pela régua, então não entram na taxa nem na receita recuperada do funil.
          </span>
        </p>
      ) : null}
    </SectionCard>
  );
}
